import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { cn } from '../lib/cn';

import { Alert, type AlertTone } from './alert';

export interface Toast {
  id: string;
  tone: AlertTone;
  title?: string;
  message: string;
}

export interface ToastInput {
  tone?: AlertTone;
  title?: string;
  message: string;
  /** Milliseconds before auto-dismiss. Pass 0 to require a manual dismiss. */
  duration?: number;
}

interface ToastContextValue {
  toast: (input: ToastInput) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/**
 * Confirmation for an action the user just took (rules §17.7).
 * Throws outside a ToastProvider, because a silently swallowed confirmation is worse
 * than a crash in development.
 */
export function useToast(): ToastContextValue {
  const value = useContext(ToastContext);
  if (value === null) {
    throw new Error('useToast must be used inside a <ToastProvider>');
  }
  return value;
}

export interface ToastProviderProps {
  children: ReactNode;
  /** Default auto-dismiss, in milliseconds. */
  defaultDuration?: number;
}

/**
 * Minimal toast stack. The viewport is a `role="region"` with `aria-live`, so a toast
 * is announced without stealing focus; errors are `assertive`, everything else polite
 * (that lives in Alert, which renders each toast).
 */
export function ToastProvider({ children, defaultDuration = 5000 }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const counter = useRef(0);

  const dismiss = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((current) => current.filter((item) => item.id !== id));
  }, []);

  const toast = useCallback(
    ({ tone = 'info', title, message, duration = defaultDuration }: ToastInput) => {
      counter.current += 1;
      const id = `toast-${String(counter.current)}`;
      setToasts((current) => [...current, { id, tone, title, message }]);
      if (duration > 0) {
        timers.current.set(
          id,
          setTimeout(() => {
            dismiss(id);
          }, duration),
        );
      }
      return id;
    },
    [defaultDuration, dismiss],
  );

  const value = useMemo(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

interface ToastViewportProps {
  toasts: Toast[];
  onDismiss: (id: string) => void;
}

function ToastViewport({ toasts, onDismiss }: ToastViewportProps) {
  return (
    /*
     * M13.7 (WCAG 4.1.3 Status Messages). The viewport is mounted for the whole life of
     * the app and IS the live region; each toast inside it is plain content
     * (`live={false}`). A live region created in the same tick as its own text is not
     * announced by VoiceOver or older JAWS, which is what an `<Alert role="status">`
     * appearing out of nothing used to be — every info, success and warning toast was
     * silently dropped. `aria-atomic="false"` so a second toast announces only itself,
     * and `aria-relevant="additions text"` so a dismissal is not read back out.
     *
     * One polite region rather than a polite and an assertive one: a toast is by
     * definition not the user's current task, and interrupting a screen reader
     * mid-sentence to say "saved" is worse than waiting. A failure that must interrupt
     * is an inline `<Alert tone="error">`, which is still `role="alert"`.
     */
    <div
      role="region"
      aria-label="Notifications"
      aria-live="polite"
      aria-atomic="false"
      aria-relevant="additions text"
      className={cn(
        'pointer-events-none fixed right-4 bottom-4 z-50 flex w-[min(360px,calc(100vw-2rem))] flex-col gap-2',
      )}
      data-testid="toast-viewport"
    >
      {toasts.map((item) => (
        <Alert
          key={item.id}
          tone={item.tone}
          title={item.title}
          live={false}
          onDismiss={() => {
            onDismiss(item.id);
          }}
          className="pointer-events-auto shadow-dropdown"
        >
          {item.message}
        </Alert>
      ))}
    </div>
  );
}
